import { z } from 'zod';

const ThemeSchema = z.object({
  board: z.object({
    bgTop: z.string(),
    bgBottom: z.string(),
    frameAlpha: z.number(),
    grainDots: z.number(),
    grainAlpha: z.number(),
  }),
  chalk: z.object({
    color: z.string(),
    glow: z.string(),
    shadowBlur: z.number(),
    jitterPx: z.number(),
  }),
  typography: z.object({
    sans: z.string(),
    mono: z.string(),
    fluidScale: z.object({
      title: z.object({ k: z.number(), min: z.number() }),
      body: z.object({ k: z.number(), min: z.number() }),
      mono: z.object({ k: z.number(), min: z.number() }),
      small: z.object({ k: z.number(), min: z.number() }),
      lineGap: z.object({ k: z.number(), min: z.number() }),
    }),
  }),
  layout: z.object({
    marginLeftK: z.number(),
    marginRightK: z.number(),
    marginTopK: z.number(),
    marginBottomK: z.number(),
  }),
});

const RuntimeSchema = z.object({
  speedFactor: z.number(),
  defaultDwellBeforeSec: z.number(),
  defaultDwellAfterSec: z.number(),
  defaultTransitionOut: z.enum(['wipe', 'fade', 'none']),
});

const NodeMetaSchema = z.object({
  dwellBeforeSec: z.number().optional(),
  dwellAfterSec: z.number().optional(),
  transitionOut: z.enum(['wipe', 'fade', 'none']).optional(),
  estimatedDurationSec: z.number().optional(),
});

const SceneAnimationStepSchema = z.object({
  step: z.number().int().positive(),
  target: z.string(),
  effect: z.string(),
  durationSec: z.number().positive().optional(),
  waitForSignal: z.boolean().optional(),
  deps: z.array(z.number().int().positive()).optional(),
  overlapSec: z.number().min(0).optional(),
  startAfterSec: z.number().min(0).optional(),
  checkpoint: z.boolean().optional(),
});

const SceneTimelineSchema = z.object({
  waitForAnimationPlan: z.boolean().optional(),
  requireSignalDone: z.boolean().optional(),
  maxWaitSec: z.number().positive().optional(),
});

const ScenePhysicsSchema = z.object({
  enabled: z.boolean().optional(),
  mode: z.string().optional(),
  params: z.record(z.any()).optional(),
});

const SceneLayersSchema = z.object({
  background: z.boolean().optional(),
  content: z.boolean().optional(),
  highlight: z.boolean().optional(),
  transition: z.boolean().optional(),
  debug: z.boolean().optional(),
});

const SceneSpecSchema = z.object({
  layout: z.object({ mode: z.string(), params: z.record(z.any()).optional() }).optional(),
  animationPlan: z.array(SceneAnimationStepSchema).optional(),
  timeline: SceneTimelineSchema.optional(),
  artifacts: z
    .array(
      z.object({
        id: z.string().optional(),
        primitive: z.string(),
        params: z.record(z.any()).optional(),
        motion: z
          .object({
            type: z.string(),
            durationSec: z.number().positive().optional(),
            loop: z.boolean().optional(),
          })
          .passthrough()
          .optional(),
      })
    )
    .optional(),
  physics: ScenePhysicsSchema.optional(),
  layers: SceneLayersSchema.optional(),
});

const InstructionalEventSchema = z.object({
  id: z.string(),
  kind: z.string(),
  payload: z.record(z.any()).default({}),
  deps: z.array(z.string()).optional(),
  checkpoint: z.boolean().optional(),
});

const NodeSpecSchema = z.object({
  id: z.string(),
  kind: z.string(),
  deps: z.array(z.string()),
  meta: NodeMetaSchema.optional(),
  scene: SceneSpecSchema.optional(),
  payload: z.any(),
});

export const LessonSpecSchema = z.object({
  version: z.number(),
  lessonId: z.string(),
  title: z.string(),
  theme: ThemeSchema,
  runtime: RuntimeSchema,
  instructionalEvents: z.array(InstructionalEventSchema).optional(),
  nodes: z.array(NodeSpecSchema),
});

export function validateLessonSpec(spec) {
  return LessonSpecSchema.parse(spec);
}
